import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

type BannerLayout =
  | 'carousel'
  | 'card-3d'
  | 'glass-hero'
  | 'neon-glow'
  | 'big-split-3d'
  | 'magazine-grid'
  | 'cinematic'
  | 'theme-showcase'
  | 'announcement'
  | 'minimal-luxury';

interface LayoutOption {
  id: BannerLayout;
  name: string;
  description: string;
  icon: string;
  tag: string;
  tagColor: string;
}

const LAYOUTS: LayoutOption[] = [
  {
    id: 'carousel',
    name: 'Classic Carousel',
    description: 'Auto-rotating banners with big rounded corners and colored shadow. Multiple promos ek saath.',
    icon: 'ri-slideshow-line',
    tag: 'Default',
    tagColor: 'bg-gray-100 text-gray-600',
  },
  {
    id: 'card-3d',
    name: '3D Card Stack',
    description: 'Stacked cards with 3D perspective, depth shadow aur glow. Ekdum premium look!',
    icon: 'ri-stack-line',
    tag: '3D Effect',
    tagColor: 'bg-orange-100 text-orange-700',
  },
  {
    id: 'glass-hero',
    name: 'Glassmorphism Hero',
    description: 'Frosted glass panels over fashion image. Blur backdrop, floating stats cards.',
    icon: 'ri-blur-off-line',
    tag: 'Glass',
    tagColor: 'bg-sky-100 text-sky-700',
  },
  {
    id: 'neon-glow',
    name: 'Neon Glow Dark',
    description: 'Dark background with neon glow effects, grid lines aur countdown timer. Futuristic!',
    icon: 'ri-flashlight-line',
    tag: 'Neon + Dark',
    tagColor: 'bg-purple-100 text-purple-700',
  },
  {
    id: 'big-split-3d',
    name: 'Big Split 3D',
    description: 'Dark split layout — left mein 3D product with glow platform, right mein bold content.',
    icon: 'ri-layout-left-line',
    tag: '3D + Big',
    tagColor: 'bg-rose-100 text-rose-700',
  },
  {
    id: 'magazine-grid',
    name: 'Magazine Grid',
    description: 'Editorial magazine style — ek bada card + 2 small cards. Bottom CTA bar.',
    icon: 'ri-layout-masonry-line',
    tag: 'Editorial',
    tagColor: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'cinematic',
    name: 'Cinematic Fullscreen',
    description: 'Full height cinematic image with letterbox bars, theme color tint aur bold typography.',
    icon: 'ri-movie-2-line',
    tag: 'Cinematic',
    tagColor: 'bg-slate-100 text-slate-700',
  },
  {
    id: 'theme-showcase',
    name: 'Inner Theme Showcase',
    description: 'Light background with theme colors — product cards, glow accents, brand feel.',
    icon: 'ri-palette-line',
    tag: 'Theme Colors',
    tagColor: 'bg-teal-100 text-teal-700',
  },
  {
    id: 'announcement',
    name: 'Announcement + Hero',
    description: 'Top scrolling marquee bar + big hero image below. Best for sales & promos.',
    icon: 'ri-megaphone-line',
    tag: 'Marquee',
    tagColor: 'bg-green-100 text-green-700',
  },
  {
    id: 'minimal-luxury',
    name: 'Minimal Luxury',
    description: 'Clean white layout with subtle gradients, stats, 3D product float aur elegant typography.',
    icon: 'ri-sparkling-line',
    tag: 'Minimal',
    tagColor: 'bg-pink-100 text-pink-700',
  },
];

// Visual preview component for each style
function LayoutPreview({ id, colors }: { id: BannerLayout; colors: { primary: string; secondary: string; accent: string } }) {
  const p = colors.primary;
  const s = colors.secondary;
  const a = colors.accent;

  if (id === 'carousel') {
    return (
      <div className="w-full h-full rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #c2410c, #dc2626)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }}></div>
        <div className="p-3 h-full flex flex-col justify-between">
          <div>
            <div className="w-12 h-3 rounded-full bg-yellow-300 mb-2"></div>
            <div className="w-20 h-4 rounded bg-white/80 mb-1"></div>
            <div className="w-16 h-3 rounded bg-white/40"></div>
          </div>
          <div className="flex gap-1">
            <div className="w-4 h-1.5 rounded-full bg-white"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
          </div>
        </div>
      </div>
    );
  }

  if (id === 'card-3d') {
    return (
      <div className="w-full h-full relative flex items-center justify-center" style={{ background: '#111' }}>
        <div className="absolute w-[80%] h-[55%] rounded-xl opacity-40" style={{ background: `linear-gradient(135deg, ${s}, ${p})`, transform: 'rotate(5deg) scale(0.88) translateY(8px)', top: '15%' }}></div>
        <div className="absolute w-[85%] h-[60%] rounded-xl opacity-60" style={{ background: `linear-gradient(135deg, ${p}, ${s})`, transform: 'rotate(-2deg) scale(0.93) translateY(4px)', top: '12%' }}></div>
        <div className="relative w-[88%] h-[65%] rounded-xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${p}, ${s})`, boxShadow: `0 8px 24px ${p}60`, top: '8%' }}>
          <div className="p-2 h-full flex flex-col justify-between">
            <div className="w-10 h-2.5 rounded-full" style={{ background: a }}></div>
            <div>
              <div className="w-16 h-3 rounded bg-white/80 mb-1"></div>
              <div className="w-10 h-2 rounded bg-white/40"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (id === 'glass-hero') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl" style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
        <div className="absolute inset-0 opacity-30" style={{ background: `linear-gradient(135deg, ${p}40, transparent)` }}></div>
        <div className="absolute bottom-2 left-2 right-2 rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <div className="flex gap-2">
            {[p, s, a].map((c, i) => (
              <div key={i} className="flex-1 rounded p-1 text-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div className="w-3 h-3 rounded-full mx-auto mb-0.5" style={{ background: c }}></div>
                <div className="w-full h-1.5 rounded bg-white/40"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-3">
          <div className="w-16 h-2.5 rounded-full mb-2" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}></div>
          <div className="w-20 h-4 rounded bg-white/80 mb-1"></div>
          <div className="w-14 h-2.5 rounded bg-white/40"></div>
        </div>
      </div>
    );
  }

  if (id === 'neon-glow') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl" style={{ background: '#050505' }}>
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `linear-gradient(${p}50 1px, transparent 1px), linear-gradient(90deg, ${p}50 1px, transparent 1px)`, backgroundSize: '16px 16px' }}></div>
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full opacity-30 blur-xl" style={{ background: p }}></div>
        <div className="p-3 h-full flex flex-col items-center justify-center text-center">
          <div className="w-16 h-2.5 rounded-full mb-2" style={{ border: `1px solid ${p}80`, boxShadow: `0 0 8px ${p}40` }}></div>
          <div className="w-20 h-4 rounded mb-2" style={{ background: `${p}30`, boxShadow: `0 0 12px ${p}40` }}></div>
          <div className="flex gap-1.5 mb-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="w-7 h-7 rounded-lg" style={{ border: `1px solid ${p}60`, boxShadow: `0 0 8px ${p}30` }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-3 h-3 rounded" style={{ background: `${p}60` }}></div>
                </div>
              </div>
            ))}
          </div>
          <div className="w-14 h-5 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})`, boxShadow: `0 0 12px ${p}60` }}></div>
        </div>
      </div>
    );
  }

  if (id === 'big-split-3d') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl grid grid-cols-2" style={{ background: '#111' }}>
        <div className="flex items-center justify-center p-2">
          <div className="relative">
            <div className="absolute -inset-2 rounded-xl opacity-40 blur-md" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
            <div className="relative w-10 h-14 rounded-xl" style={{ background: `linear-gradient(135deg, ${p}, ${s})`, transform: 'perspective(200px) rotateY(-8deg)', boxShadow: `0 8px 20px ${p}60` }}></div>
          </div>
        </div>
        <div className="flex flex-col justify-center p-2">
          <div className="w-10 h-2 rounded mb-1.5" style={{ background: `${p}30`, border: `1px solid ${p}40` }}></div>
          <div className="w-14 h-3 rounded bg-white/70 mb-1"></div>
          <div className="w-10 h-2 rounded bg-white/30 mb-2"></div>
          <div className="w-12 h-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
        </div>
      </div>
    );
  }

  if (id === 'magazine-grid') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl grid grid-cols-3 gap-0.5" style={{ background: '#000' }}>
        <div className="col-span-2 row-span-2 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${p}60, #000)` }}>
          <div className="absolute bottom-1 left-1 right-1">
            <div className="w-8 h-1.5 rounded-full mb-0.5" style={{ background: p }}></div>
            <div className="w-12 h-2 rounded bg-white/70"></div>
          </div>
        </div>
        <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${s}60, #000)` }}>
          <div className="absolute bottom-1 left-1"><div className="w-8 h-1.5 rounded bg-white/60"></div></div>
        </div>
        <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${a}60, #000)` }}>
          <div className="absolute bottom-1 left-1"><div className="w-8 h-1.5 rounded bg-white/60"></div></div>
        </div>
        <div className="col-span-3 h-4 flex items-center justify-between px-2" style={{ background: `linear-gradient(90deg, ${p}, ${s})` }}>
          <div className="w-10 h-1.5 rounded bg-white/70"></div>
          <div className="w-8 h-2.5 rounded bg-white/90"></div>
        </div>
      </div>
    );
  }

  if (id === 'cinematic') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl" style={{ background: '#000' }}>
        <div className="absolute inset-0 opacity-50" style={{ background: `linear-gradient(135deg, ${p}40, transparent)` }}></div>
        <div className="absolute top-0 left-0 right-0 h-3 bg-black z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-3 bg-black z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
        <div className="relative z-20 p-3 h-full flex flex-col justify-center">
          <div className="w-10 h-1.5 rounded mb-1.5" style={{ background: a }}></div>
          <div className="w-16 h-4 rounded bg-white/80 mb-1"></div>
          <div className="w-12 h-2.5 rounded bg-white/40 mb-2"></div>
          <div className="flex gap-1.5">
            <div className="w-10 h-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
            <div className="w-10 h-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (id === 'theme-showcase') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl p-2" style={{ background: '#fafafa', border: `1px solid ${p}20` }}>
        <div className="absolute top-0 right-0 w-16 h-16 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2" style={{ background: `radial-gradient(circle, ${p}, transparent)` }}></div>
        <div className="flex gap-2 h-full">
          <div className="flex-1 flex flex-col justify-center">
            <div className="w-10 h-1.5 rounded-full mb-1.5" style={{ background: `${p}30`, border: `1px solid ${p}40` }}></div>
            <div className="w-14 h-3 rounded mb-1" style={{ background: `${p}20` }}></div>
            <div className="w-10 h-2 rounded mb-2" style={{ background: `${p}10` }}></div>
            <div className="w-10 h-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
          </div>
          <div className="flex gap-1 items-center">
            {[p, s].map((c, i) => (
              <div key={i} className="w-8 rounded-xl overflow-hidden" style={{ boxShadow: `0 4px 12px ${c}40` }}>
                <div className="h-10" style={{ background: `linear-gradient(135deg, ${c}60, ${c}30)` }}></div>
                <div className="h-3 p-0.5" style={{ background: i === 0 ? c : '#fff' }}>
                  <div className="w-full h-1 rounded" style={{ background: i === 0 ? 'rgba(255,255,255,0.6)' : `${c}60` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (id === 'announcement') {
    return (
      <div className="w-full h-full relative overflow-hidden rounded-xl flex flex-col gap-0.5" style={{ background: '#111' }}>
        <div className="h-5 flex items-center px-2 gap-1.5 shrink-0" style={{ background: `linear-gradient(90deg, ${p}, ${s})` }}>
          <div className="w-2 h-2 rounded-full bg-white/80 shrink-0"></div>
          <div className="flex-1 h-1.5 rounded bg-white/40"></div>
          <div className="w-6 h-3 rounded bg-white/90 shrink-0"></div>
        </div>
        <div className="flex-1 relative overflow-hidden">
          <div className="absolute inset-0 opacity-40" style={{ background: `linear-gradient(90deg, rgba(0,0,0,0.9), rgba(0,0,0,0.4))` }}></div>
          <div className="relative p-2 h-full flex flex-col justify-center">
            <div className="w-14 h-3 rounded bg-white/80 mb-1"></div>
            <div className="w-10 h-2 rounded bg-white/40 mb-2"></div>
            <div className="w-10 h-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
          </div>
        </div>
      </div>
    );
  }

  // minimal-luxury
  return (
    <div className="w-full h-full relative overflow-hidden rounded-xl p-2 grid grid-cols-2" style={{ background: '#fafafa', border: `1px solid ${p}15` }}>
      <div className="absolute top-0 right-0 w-12 h-12 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${p}, transparent)` }}></div>
      <div className="flex flex-col justify-center">
        <div className="w-8 h-1 rounded mb-1.5" style={{ background: s }}></div>
        <div className="w-14 h-3 rounded mb-1" style={{ background: `${p}80` }}></div>
        <div className="w-6 h-0.5 rounded mb-2" style={{ background: `linear-gradient(90deg, ${p}, ${s})` }}></div>
        <div className="flex gap-2 mb-2">
          {['500+', '30m', '4.8'].map(v => (
            <div key={v}>
              <div className="w-5 h-2 rounded" style={{ background: `${p}60` }}></div>
            </div>
          ))}
        </div>
        <div className="w-10 h-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
      </div>
      <div className="flex items-center justify-center">
        <div className="relative">
          <div className="absolute -inset-2 rounded-xl opacity-20 blur-md" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
          <div className="relative w-10 h-14 rounded-xl" style={{ background: `linear-gradient(135deg, ${p}40, ${s}40)`, boxShadow: `0 8px 20px ${p}40` }}></div>
          <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-10 h-3 rounded-full" style={{ background: `linear-gradient(135deg, ${p}, ${s})` }}></div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export default function BannerLayoutPanel({ onToast }: Props) {
  const [currentLayout, setCurrentLayout] = useState<BannerLayout>('carousel');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<BannerLayout>('carousel');

  // Use theme colors for previews
  const previewColors = { primary: '#f97316', secondary: '#ea580c', accent: '#fbbf24' };

  useEffect(() => {
    loadLayout();
  }, []);

  const loadLayout = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('banner_layouts')
        .select('layout_style')
        .eq('id', 'main')
        .maybeSingle();
      if (error) {
        console.error('Load layout error:', error);
      } else if (data?.layout_style) {
        console.log('Layout loaded:', data.layout_style);
        setCurrentLayout(data.layout_style as BannerLayout);
        setSelectedLayout(data.layout_style as BannerLayout);
      }
    } catch (err) {
      console.error('Load layout exception:', err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('banner_layouts')
        .upsert({ 
          id: 'main', 
          layout_style: selectedLayout, 
          updated_at: new Date().toISOString() 
        }, 
        { onConflict: 'id' }
        )
        .select();
        
      if (error) {
        console.error('Save layout error:', error);
        throw error;
      }
      
      console.log('Layout saved successfully:', data);
      setCurrentLayout(selectedLayout);
      onToast('Banner style update ho gaya! Home page pe dekho.', 'success');
    } catch (err) {
      console.error('Save layout exception:', err);
      onToast('Save nahi hua. Dobara try karo.', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
      </div>
    );
  }

  const currentOption = LAYOUTS.find(l => l.id === currentLayout);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <i className="ri-layout-masonry-line text-orange-600 text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Banner Visual Style</p>
            <p className="text-gray-400 text-xs">Size, shadow, 3D look, glow — sab yahan se control karo</p>
          </div>
        </div>
      </div>

      {/* Current Layout Badge */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
            <i className={`${currentOption?.icon} text-orange-500 text-xl`}></i>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Current Style</p>
            <p className="text-gray-900 font-bold text-lg">{currentOption?.name}</p>
          </div>
        </div>
      </div>

      {/* Layout Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {LAYOUTS.map((layout) => (
          <div
            key={layout.id}
            onClick={() => setSelectedLayout(layout.id)}
            className={`relative bg-white rounded-2xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
              selectedLayout === layout.id
                ? 'border-orange-500 ring-2 ring-orange-200'
                : 'border-gray-100 hover:border-orange-200'
            }`}
          >
            {/* Live CSS Preview */}
            <div className="h-28 overflow-hidden bg-gray-50 p-2">
              <LayoutPreview id={layout.id} colors={previewColors} />
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${selectedLayout === layout.id ? 'bg-orange-100' : 'bg-gray-100'}`}>
                  <i className={`${layout.icon} ${selectedLayout === layout.id ? 'text-orange-600' : 'text-gray-500'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-gray-900 font-bold text-sm">{layout.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${layout.tagColor}`}>{layout.tag}</span>
                    {currentLayout === layout.id && (
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">Active</span>
                    )}
                    {selectedLayout === layout.id && currentLayout !== layout.id && (
                      <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">Selected</span>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed">{layout.description}</p>
                </div>
              </div>
            </div>

            {/* Selection Indicator */}
            {selectedLayout === layout.id && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur-sm rounded-2xl border border-gray-100 p-4 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-gray-500 text-xs">
              {selectedLayout !== currentLayout
                ? 'Apply karne se home page pe turant reflect hoga'
                : 'Koi changes nahi hain'}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || selectedLayout === currentLayout}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-white font-bold text-sm hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
            ) : (
              <><i className="ri-save-line"></i>Apply Style</>
            )}
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-box-3-line text-orange-500"></i>
            <p className="text-orange-900 font-bold text-sm">3D Styles</p>
          </div>
          <p className="text-orange-700 text-xs">Card Stack aur Big Split 3D mein real depth aur shadow effects hain.</p>
        </div>
        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-blur-off-line text-slate-500"></i>
            <p className="text-slate-900 font-bold text-sm">Glass Effects</p>
          </div>
          <p className="text-slate-700 text-xs">Glassmorphism style mein frosted glass panels aur blur backdrop hai.</p>
        </div>
        <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-palette-line text-emerald-500"></i>
            <p className="text-emerald-900 font-bold text-sm">Theme Colors</p>
          </div>
          <p className="text-emerald-700 text-xs">Saare styles automatically aapke selected theme ke colors use karte hain.</p>
        </div>
      </div>
    </div>
  );
}
